import { getConfig } from '@/modules/common/helpers/config';
import { API_TIMEOUTS } from '../constant';
import { IProjectApiConnector } from './IProjectApiConnector';
import { ProjectService } from '@thive/fddm-types';
import { BaseConnector } from '@/modules/api/BaseConnector';

export class ProjectApiConnector extends BaseConnector implements IProjectApiConnector {
  private static instance: IProjectApiConnector;
  private static baseUrl = getConfig().api.project.url;
  private static scopes = [getConfig().api.project.scope];

  public static getInstance(): IProjectApiConnector {
    if (!ProjectApiConnector.instance) {
      ProjectApiConnector.instance = new ProjectApiConnector();
    }
    return ProjectApiConnector.instance;
  }

  private constructor() {
    super(ProjectApiConnector.baseUrl, ProjectApiConnector.scopes, API_TIMEOUTS.project);
  }

  async getProjects(): Promise<ProjectService['getProjects']['response']> {
    return (await this.get<ProjectService['getProjects']['response']>('/')) ?? [];
  }

  async getProject({
    projectId,
  }: ProjectService['getProject']['request']): Promise<ProjectService['getProject']['response']> {
    return this.get<ProjectService['getProject']['response']>(`/${projectId}`, {
      requiredErrorMessage: `Failed to get project ${projectId}`,
    });
  }

  async getLayoutAlternatives({
    projectId,
  }: ProjectService['getLayoutAlternatives']['request']): Promise<
    ProjectService['getLayoutAlternatives']['response']
  > {
    return (
      (await this.get<ProjectService['getLayoutAlternatives']['response']>(
        `/${projectId}/alternatives`,
      )) ?? []
    );
  }

  async getLayoutAlternative({
    projectId,
    alternativeId,
  }: ProjectService['getLayoutAlternative']['request']): Promise<
    ProjectService['getLayoutAlternative']['response']
  > {
    return this.get<ProjectService['getLayoutAlternative']['response']>(
      `/${projectId}/alternatives/${alternativeId}`,
      {
        requiredErrorMessage: `Failed to get layout alternative ${alternativeId} for project ${projectId}`,
      },
    );
  }

  async createLayoutAlternative({
    projectId,
    alternativeName,
  }: ProjectService['createLayoutAlternative']['request']): Promise<
    ProjectService['createLayoutAlternative']['response']
  > {
    return this.post<
      ProjectService['createLayoutAlternative']['response'],
      { alternativeName: string }
    >(
      `/${projectId}/alternatives`,
      { alternativeName },
      {
        requiredErrorMessage: `Failed to create layout alternative for project ${projectId}`,
      },
    );
  }

  async duplicateLayoutAlternative({
    projectId,
    alternativeId,
    newAlternativeName,
  }: ProjectService['duplicateLayoutAlternative']['request']): Promise<
    ProjectService['duplicateLayoutAlternative']['response']
  > {
    return this.post<
      ProjectService['duplicateLayoutAlternative']['response'],
      { alternativeName: string }
    >(
      `/${projectId}/alternatives/${alternativeId}/duplicate`,
      { alternativeName: newAlternativeName },
      {
        requiredErrorMessage: `Failed to duplicate layout alternative ${alternativeId} for project ${projectId}`,
      },
    );
  }

  async deleteLayoutAlternative({
    projectId,
    alternativeId,
  }: ProjectService['deleteLayoutAlternative']['request']): Promise<
    ProjectService['deleteLayoutAlternative']['response']
  > {
    return this.delete<ProjectService['deleteLayoutAlternative']['response']>(
      `/${projectId}/alternatives/${alternativeId}`,
      {
        requiredErrorMessage: `Failed to delete layout alternative ${alternativeId} for project ${projectId}`,
      },
    );
  }

  async getFloorplanRevisionMetaDatas({
    projectId,
    alternativeId,
  }: ProjectService['getFloorplanRevisionMetaDatas']['request']): Promise<
    ProjectService['getFloorplanRevisionMetaDatas']['response']
  > {
    return (
      (await this.get<ProjectService['getFloorplanRevisionMetaDatas']['response']>(
        `/${projectId}/alternatives/${alternativeId}/revisions`,
      )) ?? []
    );
  }

  async getFloorplanRevision({
    projectId,
    alternativeId,
    revisionId,
  }: ProjectService['getFloorplanRevision']['request']): Promise<
    ProjectService['getFloorplanRevision']['response']
  > {
    return this.get<ProjectService['getFloorplanRevision']['response']>(
      `/${projectId}/alternatives/${alternativeId}/revisions/${revisionId}`,
      {
        requiredErrorMessage: `Failed to get floorplan revision ${revisionId} for project ${projectId}`,
        // Avoid it being aborted by a concurrent identical request.
        // I.e. race with AppService.loadRevision (via useDigitalTwin) when simRevisionId === revisionId.
        // 1. This call registers an AbortController in BaseConnector.runLatest under key `get:<revision-url>`.
        // 2. loadRevision then calls getFloorplanRevision on the same singleton connector with the same key.
        // 3. runLatest aborts the earlier request (this one); BaseConnector.get catches the cancel and returns undefined.
        // 4. `revision` is undefined here, so accessing `revision.design` throws, while loadRevision's request completes normally.
        skipLatest: true,
      },
    );
  }

  async getLatestFloorplanRevision({
    projectId,
    alternativeId,
  }: ProjectService['getLatestFloorplanRevision']['request']): Promise<
    ProjectService['getLatestFloorplanRevision']['response']
  > {
    return this.get<ProjectService['getLatestFloorplanRevision']['response']>(
      `/${projectId}/alternatives/${alternativeId}/revisions/latest`,
      {
        requiredErrorMessage: `Failed to get latest floorplan revision for project ${projectId} and alternative ${alternativeId}`,
      },
    );
  }

  async saveFloorplanRevisionDraft({
    projectId,
    alternativeId,
    revision,
  }: ProjectService['saveFloorplanRevisionDraft']['request']): Promise<
    ProjectService['saveFloorplanRevisionDraft']['response']
  > {
    return this.put<ProjectService['saveFloorplanRevisionDraft']['response']>(
      `/${projectId}/alternatives/${alternativeId}/revisions/latest`,
      revision,
      {
        requiredErrorMessage: `Failed to save floorplan revision draft for project ${projectId} and alternative ${alternativeId}`,
      },
    );
  }

  async finalizeLatestFloorplanRevision({
    projectId,
    alternativeId,
  }: ProjectService['finalizeLatestFloorplanRevision']['request']): Promise<
    ProjectService['finalizeLatestFloorplanRevision']['response']
  > {
    return this.post<ProjectService['finalizeLatestFloorplanRevision']['response']>(
      `/${projectId}/alternatives/${alternativeId}/revisions/latest/finalize`,
      undefined,
      {
        requiredErrorMessage: `Failed to finalize latest floorplan revision for project ${projectId} and alternative ${alternativeId}`,
      },
    );
  }

  async restoreFloorplanRevision({
    projectId,
    alternativeId,
    revisionId,
  }: ProjectService['restoreFloorplanRevision']['request']): Promise<
    ProjectService['restoreFloorplanRevision']['response']
  > {
    return this.post<ProjectService['restoreFloorplanRevision']['response']>(
      `/${projectId}/alternatives/${alternativeId}/revisions/${revisionId}/restore`,
      undefined,
      {
        requiredErrorMessage: `Failed to restore floorplan revision ${revisionId} for project ${projectId} and alternative ${alternativeId}`,
      },
    );
  }
}
